import { GBasePass, GetBasePassUniformParametersList, Material, RopePositionsTexture } from "../../../basePass";
import { GAudioEngine } from "../../../core/audioEngine";
import { GUserInputDesc } from "../../../core/input";
import { Shader } from "../../../core/shaderUtils";
import { SpatialControlPointWS } from "../../../core/spatialController";
import { GTexturePool } from "../../../core/texturePool";
import { GStack, GetVec2, GetVec3, Vector2, Vector3 } from "../../../core/types";
import { GTime, MathClamp, MathGetVec3Length, MathLerp, MathMapToRangeClamped } from "../../../core/utils";
import { GLBindUniformBuffer, GLSetTexture, GLSetVec3 } from "../../../helpers/glHelper";
import { TransformFromNDCToWorld } from "../../../helpers/transform";
import { PhysGenerateRope, PhysPoint, PhysicsBody } from "../../../physics";
import { RCombinerPass } from "../../../postprocess";
import { GPostProcessPasses } from "../../../renderingMain";
import { GRibbonsRenderer, GenerateSplineTangents } from "../../../ribbons";
import {
    GetShaderSourceProceduralLineRenderVS,
    GetShaderSourceProceduralRectRenderVS,
    GetShaderSourceProceduralShapeRenderPS,
} from "../../../shaders/shaderBasePass";
import { CommonRenderingResources } from "../../../shaders/shaderConfig";
import { SceneBase } from "../../scene";
import { GHallwaySceneCSS } from "../hallway/hallwayCSS";
import { PayneSceneLightSwitch } from "./lightSwitch";
import { ESubSceneState } from "./lightSwitchScripts";

export class SwitchRopeTool {
    InitialHandlePosWS = new Vector3(0, 0, 0);
    RopeOriginPos = new Vector3(0, 0, 0);
    RopeLength: number;

    bEnalbed = true;

    HandleExtentSize = GetVec3(0.1, 0.35, 0.1);

    //Controller
    SpatialController;

    Rope: PhysicsBody;
    RopeHandlePoint: PhysPoint;
    RopeOriginPoint: PhysPoint;

    bSwitchClickOn = false;
    TimeWhenLastSwitchClickDisabled = 0.0;

    SwitchHandleMeshMaterial = new Material();
    SwitchRopeMeshMaterial = new Material();

    constructor(
        gl: WebGL2RenderingContext,
        sceneBoundsCenter: Vector3,
        sceneBoundsExtent: Vector3,
        scene: PayneSceneLightSwitch,
    ) {
        this.SpatialController = new SpatialControlPointWS(GetVec3(2.0, 1.0, -4.0));
        this.SpatialController.BoundsExtent.Set(this.HandleExtentSize);

        const RopeNumSegments = 10;

        //Rope
        {
            const lengthTrunc = 1.0;
            this.RopeOriginPos.Set3(4.5, sceneBoundsCenter.y + sceneBoundsExtent.y + 1.0 - lengthTrunc, -5.0);
            this.RopeLength = sceneBoundsExtent.y - lengthTrunc + 0.25;
            this.Rope = PhysGenerateRope(this.RopeOriginPos, this.RopeLength, RopeNumSegments + 1);
            this.Rope.ConstantForce.y = -120;
            this.RopeHandlePoint = this.Rope.Points.at(this.Rope.Points.length - 1)!;
            this.RopeOriginPoint = this.Rope.Points[0]!;

            SceneBase.UniformBuffer.Params.SpotlightTargetPos2.Set(this.RopeHandlePoint.PositionCur);

            this.Rope.NumSolverIterations = 32;
            this.Rope.VelocityDampingScale.y = 20.0;
            this.Rope.VelocityDampingScale.x = 3.15;
            this.Rope.VelocityDampingScale.z = 3.15;
        }

        this.InitialHandlePosWS.Set(this.RopeHandlePoint.PositionCur);
        this.SpatialController.PositionWS.Set(this.RopeHandlePoint.PositionCur);

        this.InitRenderResources(gl, scene);
    }

    SwitchOffsetLength = 1.25;
    SwitchThres = 0.4;

    HandleSwitchClick() {
        //offset rope origin
        const ro = this.RopeOriginPoint;
        const newPos = GStack.TempVec3_4;
        newPos.Set(this.RopeOriginPos);
        newPos.y -= this.SwitchOffsetLength;
        ro.InitNewPos(newPos);
    }

    OnUpdate(scene: PayneSceneLightSwitch) {
        if (!this.bEnalbed) return;

        const phoneState = scene.CurSceneState == ESubSceneState.Phone;

        this.SpatialController.OnUpdate();

        const audioContext = GAudioEngine.Instance!.Context;

        if (
            this.SpatialController.bIntersectionThisFrame &&
            !this.SpatialController.bIntersectionPrevFrame &&
            scene.CurSceneState == ESubSceneState.Phone
        ) {
            if (audioContext && scene.SoundPainkiller.Buffer) {
                scene.SoundPainkiller.Play(audioContext, true);
            }
        }
        if (Math.abs(this.RopeHandlePoint.PrevVelocity.x) > 0.05 || this.SpatialController.bSelectedThisFrame) {
            if (audioContext && scene.SoundChain.Buffer) {
                scene.SoundChain.Play(audioContext, true, Math.random() * scene.SoundChain.Buffer.duration * 0.5);
            }
        }

        const ropeL = GStack.TempVec3;
        ropeL.Set(this.RopeHandlePoint.PositionCur);
        ropeL.Negate(this.RopeOriginPos);
        const d = MathGetVec3Length(ropeL);

        const pointBeforehandle = this.Rope.Points.at(this.Rope.Points.length - 2)!;

        if (!this.SpatialController.bDragState && !phoneState) {
            if (GTime.Cur % 20.0 < 0.05) {
                const offsetDir = 0.015 * (0.5 + Math.random() * 0.5);
                this.RopeHandlePoint.PositionCur.x += offsetDir;
                this.Rope.Points.at(this.Rope.Points.length - 2)!.PositionCur.x += offsetDir * 0.5;
            }
        }

        /* if (this.SpatialController.bReleasedThisFrame) {
           
        } */

        let roomNoiseAddGain = 0.0;
        if (this.SpatialController.bDragState) {
            //pull sound
            {
                if (!this.bSwitchClickOn) {
                    roomNoiseAddGain = MathMapToRangeClamped(
                        d,
                        this.RopeLength,
                        this.RopeLength + this.SwitchThres,
                        0.0,
                        0.2,
                    );
                }
            }
            if (this.bSwitchClickOn) {
                if (d > this.RopeLength + 0.8 + this.SwitchOffsetLength) {
                    this.SpatialController.bDragState = false;
                }
                if (d < this.RopeLength + this.SwitchThres) {
                    this.RopeOriginPoint.InitNewPos(this.RopeOriginPos);
                    this.bSwitchClickOn = false;
                }
                if (GUserInputDesc.InputPosCurNDC.y < -0.9) {
                    this.SpatialController.bDragState = false;
                }
            } else {
                if (d > this.RopeLength + this.SwitchThres || GUserInputDesc.InputPosCurNDC.y < -0.7) {
                    this.bSwitchClickOn = true;
                    this.HandleSwitchClick();
                    if (!phoneState) {
                        scene.bNewSubSceneStateThisFrame = true;
                        scene.PlayHeartbeat();
                    } else {
                        RCombinerPass.EFullscreenVFXMode = 1;
                        GHallwaySceneCSS.guiltTextSetVisible(true);
                        GHallwaySceneCSS.guiltTextQuoteIndex(1);
                        scene.SoundOnFireOn();
                        setTimeout(() => {
                            GHallwaySceneCSS.guiltTextSetVisible(false);
                            RCombinerPass.EFullscreenVFXMode = 0;
                            scene.SoundOnFireOff();
                        }, 0.5 * 1000);

                        this.bEnalbed = false;
                        scene.MeshPills.bCanDraw = false;
                    }
                }
            }
        } else {
            //this.RopeOriginPoint.InitNewPos(this.RopeOriginPos);

            if (this.bSwitchClickOn) {
                this.bSwitchClickOn = false;
                this.TimeWhenLastSwitchClickDisabled = GTime.Cur;

                /* const offsetDir = (Math.random() - 0.5) * 0.25;
                this.RopeHandlePoint.PositionCur.x += offsetDir;
                this.Rope.Points.at(this.Rope.Points.length - 2)!.PositionCur.x += offsetDir; */
            }

            //Make sure handle isnt upside down

            const timeSinceLastSwitchDisabled = GTime.Cur - this.TimeWhenLastSwitchClickDisabled;
            if (timeSinceLastSwitchDisabled < 0.1) {
                this.RopeHandlePoint.PositionCur.x = pointBeforehandle.PositionCur.x;
            }
        }

        const roomNoiseSoundGain = scene.SoundRoomNoise.Gain;
        if (roomNoiseSoundGain) {
            roomNoiseSoundGain.gain.value = scene.RoomNoiseInitGain + roomNoiseAddGain;
        }

        this.RopeHandlePoint.PositionCur.y = Math.min(
            pointBeforehandle.PositionCur.y,
            this.RopeHandlePoint.PositionCur.y,
        );

        //Rope Origin Constraint
        {
            const curDir = GStack.TempVec3_2;
            curDir.Set(this.RopeOriginPoint.PositionCur);
            curDir.Negate(this.RopeOriginPos);
            const delta = MathGetVec3Length(curDir);
            curDir.Normalize();
            if (!this.bSwitchClickOn) {
                curDir.Mul(delta * 2.0 * 0.1);
            } else {
                curDir.Mul(delta * 0.05 * 0.1 * 0.01);
            }
            this.RopeOriginPoint.PositionCur.Negate(curDir);
            this.RopeOriginPoint.PositionPrev = this.RopeOriginPoint.PositionCur;
        }

        if (this.SpatialController.bDragState) {
            this.RopeHandlePoint.PositionCur.Set(this.SpatialController.PositionWS);
        } else {
            this.SpatialController.PositionWS.Set(this.RopeHandlePoint.PositionCur);
            //Handle magnet
            {
                const pointerPosWS = GStack.TempVec3_3;
                TransformFromNDCToWorld(
                    GUserInputDesc.InputPosCurNDC,
                    this.RopeHandlePoint.PositionCur.z - SceneBase.UniformBuffer.Params.CameraPosition.z,
                    pointerPosWS,
                );
                const l = GStack.TempVec3_2;
                l.Set(pointerPosWS);
                l.Negate(this.RopeHandlePoint.PositionCur);
                const dToPointer = MathGetVec3Length(l);
                const tt = 1.0 - MathClamp(dToPointer / 1, 0.0, 1.0);
                l.Normalize();
                l.Mul(dToPointer * 0.01 * tt);
                this.RopeHandlePoint.Translate(l);
            }
        }

        //make sure handle keeps straight angle
        const p1 = this.Rope.Points.at(this.Rope.Points.length - 2)!.PositionCur;
        p1.z = MathLerp(this.RopeHandlePoint.PositionCur.z, p1.z, 0.5);
        if (this.SpatialController.bDragState) {
            p1.x = this.RopeHandlePoint.PositionCur.x;
        }

        this.Rope.OnUpdateBase();
    }

    //Render Resources
    ShaderHandleRender!: Shader;
    ShaderRopeRender!: Shader;
    ShaderRopeRenderHotline!: Shader;

    InitRenderResources(gl: WebGL2RenderingContext, scene: PayneSceneLightSwitch) {
        this.ShaderHandleRender = new Shader(
            gl,
            GetShaderSourceProceduralLineRenderVS(),
            GetShaderSourceProceduralShapeRenderPS(scene.SceneSDFDescPaintingScene!, false),
            GetBasePassUniformParametersList,
        );
        this.ShaderRopeRender = new Shader(
            gl,
            GetShaderSourceProceduralRectRenderVS(),
            GetShaderSourceProceduralShapeRenderPS(scene.SceneSDFDescPaintingScene!, true),
            GetBasePassUniformParametersList,
        );

        this.ShaderRopeRenderHotline = new Shader(
            gl,
            GetShaderSourceProceduralRectRenderVS(),
            GetShaderSourceProceduralShapeRenderPS(scene.SceneSDFDescPhoneScene!, true),
            GetBasePassUniformParametersList,
        );

        const matName = `brushMetal`;
        this.SwitchHandleMeshMaterial.InitTextures(gl, matName + `DFS`, matName + `NRM`, matName + `RGH_R8`, false);
        this.SwitchRopeMeshMaterial.AlbedoTexture = this.SwitchHandleMeshMaterial.AlbedoTexture;
        this.SwitchRopeMeshMaterial.AlbedoTexture = GTexturePool.CreateTexture(
            gl,
            false,
            `brushMetal5DFS`,
            true,
            true,
            false,
            null,
        );

        this.SwitchRopeMeshMaterial.NormalTexture = this.SwitchHandleMeshMaterial.NormalTexture;
        this.SwitchRopeMeshMaterial.RoughnessTexture = this.SwitchHandleMeshMaterial.RoughnessTexture;
        this.SwitchRopeMeshMaterial.Metalness = 0.75;

        this.RopePositionsArr = new Array(this.kRopeArraySizeMax).fill(null).map(() => new Vector3(0, 0, 0));

        //this.CreateRopePositionsTexture(gl);
    }

    BindCommonRenderResources(gl: WebGL2RenderingContext, curShader: Shader, material: Material) {
        const uniformsLocation = curShader.UniformParametersLocationList;
        gl.uniform1i(uniformsLocation.LightprobesGridSize, SceneBase.LightprobesManager.NumProbes.x);

        let textureBindIndex = 1;
        GLSetTexture(gl, uniformsLocation.BRDF_LUT, GBasePass.GInstance!.TextureIntegratedBRDF, textureBindIndex++);
        GLSetTexture(
            gl,
            uniformsLocation.PrevFrameSceneColor,
            GPostProcessPasses.Bloom!.DownsampleRTMipArr![1],
            textureBindIndex++,
        );
        GLSetTexture(
            gl,
            uniformsLocation.PrevFrameSceneColorBlur,
            GPostProcessPasses.Bloom!.DownsampleRTMipArr![2],
            textureBindIndex++,
        );

        if (SceneBase.GpCurProjectorTexture) {
            GLSetTexture(
                gl,
                uniformsLocation.ProjectorTexture,
                //SceneBase.ProjectorTexture.InnerTexture,
                //GVectorField.GInstance!.GetCurTextureDensity(),
                SceneBase.GpCurProjectorTexture,
                textureBindIndex++,
            );
        }

        gl.uniform1i(uniformsLocation.EUseLightprobe, 1);
        GLSetTexture(
            gl,
            curShader.UniformParametersLocationList.SHCoefficientsTexture,
            SceneBase.LightprobesManager.TextureSHCoeff,
            textureBindIndex++,
        );
        {
            gl.uniform1f(uniformsLocation.TextureScale, material.TextureScale);
            gl.uniform1f(uniformsLocation.Brightness, material.Brightness);
            gl.uniform1f(uniformsLocation.Metalness, material.Metalness);
            gl.uniform1i(uniformsLocation.bEmitter, material.bisEmmiter ? 1 : 0);

            GLSetTexture(gl, uniformsLocation.AlbedoTexture, material.AlbedoTexture, textureBindIndex++);
            GLSetTexture(gl, uniformsLocation.NormalTexture, material.NormalTexture, textureBindIndex++);
            GLSetTexture(gl, uniformsLocation.RoughnessTexture, material.RoughnessTexture, textureBindIndex++);
        }
        GLBindUniformBuffer(
            gl,
            SceneBase.UniformBuffer.BufferGPU!,
            curShader.ShaderProgram,
            uniformsLocation.SceneUniformBuffer,
        );

        return textureBindIndex;
    }

    RenderHandle(gl: WebGL2RenderingContext, lineStart: Vector3, LineEnd: Vector3, lineThickness: Vector2) {
        const curShader = this.ShaderHandleRender;
        const uniformsLocation = curShader.UniformParametersLocationList;
        gl.useProgram(curShader.ShaderProgram);
        gl.bindVertexArray(CommonRenderingResources.PlaneShapeVAO);

        GLSetVec3(gl, uniformsLocation.LineStart, lineStart);
        GLSetVec3(gl, uniformsLocation.LineEnd, LineEnd);
        gl.uniform2f(uniformsLocation.LineScale, lineThickness.x, lineThickness.y);

        const index = this.BindCommonRenderResources(gl, curShader, this.SwitchHandleMeshMaterial);

        GLSetTexture(gl, uniformsLocation.RopePositionsTexture, RopePositionsTexture, index);

        gl.drawArrays(gl.TRIANGLES, 0, 6);
    }

    kRopeArraySizeMax = 100;
    RopePositionsArr!: Vector3[];

    RenderRopeSpheres(gl: WebGL2RenderingContext, bPhoneScene: boolean) {
        let numSpheresToRender = 0;

        const subSphereRadius = 0.03;
        const curDir = new Vector3(0, 0, 0);
        const curSpawnPos = new Vector3(0, 0, 0);
        let additivelengthOffset = 0;

        //const color = new Vector3(1.0, 0.2, 0.2);

        for (let p = this.Rope.Points.length - 1; p > 0; p--) {
            const p0 = GStack.TempVec3;
            p0.Set(this.Rope.Points[p].PositionCur);
            const p1 = this.Rope.Points[p - 1].PositionCur;

            {
                curDir.Set(p1);
                curDir.Negate(p0);
                curDir.Normalize();
                curDir.Mul(additivelengthOffset);
                p0.Add(curDir);
            }

            const l = GStack.TempVec3_2;
            l.Set(p1);
            l.Negate(p0);
            const segmentLength = MathGetVec3Length(l);
            l.Normalize();
            l.Mul(subSphereRadius);

            const numSpheres = Math.ceil(segmentLength / (subSphereRadius * 2));
            additivelengthOffset = numSpheres * subSphereRadius * 2 - segmentLength;

            curSpawnPos.Set(p0);
            curSpawnPos.Add(l);

            for (let s = 0; s < numSpheres; s++) {
                this.RopePositionsArr[numSpheresToRender].Set(curSpawnPos);

                //GSimpleShapesRenderer.GInstance!.RenderPoint(gl, curSpawnPos, subSphereRadius, color, true);
                numSpheresToRender++;

                curSpawnPos.Add(l);
                curSpawnPos.Add(l);

                if (numSpheresToRender > this.kRopeArraySizeMax - 5) {
                    console.error("Light Cord Render To Many Instances");
                    p = 0;
                    break;
                }
            }
        }

        SceneBase.UniformBuffer.Params.SwitchRopeNumSpheres.x = numSpheresToRender;
        SceneBase.UniformBuffer.Params.SwitchRopeSphereRadius.x = subSphereRadius;

        //upload sphere data to gpu
        const spherePosData = new Float32Array(numSpheresToRender * 3);
        for (let i = 0; i < numSpheresToRender; i++) {
            const spherePos = this.RopePositionsArr[i];
            const writePos = i * 3;
            spherePosData[writePos] = spherePos.x;
            spherePosData[writePos + 1] = spherePos.y;
            spherePosData[writePos + 2] = spherePos.z;

            //GSimpleShapesRenderer.GInstance!.RenderPoint(gl, spherePos, subSphereRadius, color, true);
        }

        gl.bindTexture(gl.TEXTURE_2D, RopePositionsTexture);
        gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, spherePosData.length, 1, gl.RED, gl.FLOAT, spherePosData);

        {
            let curShader = this.ShaderRopeRender;
            if (bPhoneScene) {
                curShader = this.ShaderRopeRenderHotline;
            }
            const uniformsLocation = curShader.UniformParametersLocationList;
            gl.useProgram(curShader.ShaderProgram);
            gl.bindVertexArray(CommonRenderingResources.PlaneShapeVAO);

            const bindIndex = this.BindCommonRenderResources(gl, curShader, this.SwitchRopeMeshMaterial);

            GLSetTexture(gl, uniformsLocation.RopePositionsTexture, RopePositionsTexture, bindIndex);

            gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, numSpheresToRender);
        }
    }

    Render(gl: WebGL2RenderingContext, scene: PayneSceneLightSwitch) {
        if (!this.bEnalbed) return;

        //this.RenderRope(gl);
        this.RenderRopeSpheres(gl, scene.CurSceneState == ESubSceneState.Phone);

        if (scene.CurSceneState != ESubSceneState.Phone) {
            const p1 = this.Rope.Points.at(this.Rope.Points.length - 2)!.PositionCur;
            const p0 = this.RopeHandlePoint.PositionCur;
            const handleDir = GStack.TempVec3_2;
            handleDir.Set(p1);
            handleDir.Negate(p0);
            handleDir.Normalize();
            handleDir.Mul(this.HandleExtentSize.y);
            const handleStart = GStack.TempVec3_3;
            const handleEnd = GStack.TempVec3_4;
            handleStart.Set(p0);
            handleEnd.Set(p0);
            handleStart.Add(handleDir);
            handleEnd.Negate(handleDir);

            SceneBase.UniformBuffer.Params.LightSwitchHandlePos.Set(p0);

            this.RenderHandle(
                gl,
                handleStart,
                handleEnd,
                GetVec2(this.HandleExtentSize.x * 0.7, this.HandleExtentSize.x),
            );
        }

        /* 
		const color = GStack.TempVec3;
        if (this.SpatialController.bDragState) {
            color.Set3(1.0, 0.9, 0.9);
        } else if (this.SpatialController.bIntersectionThisFrame) {
            color.Set3(1.0, 0.5, 0.5);
        }
       if (this.SpatialController.bIntersectionThisFrame) {
            GSimpleShapesRenderer.GInstance!.RenderPoint(
                gl,
                this.SpatialController.IntersectionPosWS,
                0.05,
                color,
                false,
            );
        } */

        //GSimpleShapesRenderer.GInstance!.RenderLine(gl, handleStart, handleEnd, this.HandleExtentSize.x, color);
    }

    RenderRope(gl: WebGL2RenderingContext) {
        //Generate positions arr
        const posArr: Vector3[] = [];
        const velArr: Vector3[] = [];

        for (let i = 0; i < this.Rope.Points.length; i++) {
            posArr.push(this.Rope.Points[i].PositionCur);
            velArr.push(new Vector3(0, 0, 0));
        }

        GenerateSplineTangents(posArr, velArr);

        GRibbonsRenderer.GInstance?.Render(gl, posArr, velArr, GetVec3(0.5, 0.5, 0.5));
    }
}
